import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Loader,
  Save,
  ArrowLeft,
  Sparkles,
  Users,
  Calendar,
  Package,
  IndianRupee,
  CreditCard,
  FileText,
  Info,
  Receipt,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { getApiErrorMessage } from "../../../atoms/Utlis/getApiErrorMessage";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import {
  cleanPhone,
  PageContainer,
  FormScrollContainer,
  PageTitle,
  FormHero,
  FormHeroGlow,
  FormHeroRow,
  FormBackButton,
  FormHeroText,
  FormHeroBadge,
  FormHeroSubtitle,
  FormCard,
  SectionCard,
  SectionHeading,
  SectionIcon,
  SectionHeadingBody,
  SectionHeadingTitle,
  SectionHeadingSub,
  RowGrid,
  FormGroup,
  FormLabel,
  TextInput,
  TextAreaInput,
  DisabledInput,
  GrandTotalDisplay,
  FooterButtonsRow,
  SubmitHint,
  SaveButton,
  Req,
  FormSelect,
  WithoutPriceBanner,
  WithoutPriceToggleWrapper,
  WithoutPriceText,
  WithoutPriceInfoBox,
  ToggleSwitch,
} from "../formSharedStyles";
import styled from "styled-components";
import LineItemEditor from "./components/LineItemEditor";
import InstallmentEditor from "./components/InstallmentEditor";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import {
  createInvoiceAPI,
  updateInvoiceAPI,
  getInvoiceByIdAPI,
  getInvoiceNextNumberAPI,
  createInvoiceFromOrderAPI,
  createPaymentLinkAPI,
  sharePaymentLinkWhatsAppAPI
} from "../../../../Redux/ApiCalls/Dashboard/InvoiceAPI";
import { getHostUrl } from "../../../../Redux/ApiCalls/getHostUrl";
import type { InvoiceWritePayload, InvoiceInstallment } from "../../../../Data/invoice.dto";

const EndFooter = styled(FooterButtonsRow)`
  position: static;
  margin-top: 20px;
  margin-bottom: 8px;
`;

const InvoiceFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { submitting } = useAppSelector(state => state.invoice);
  const { business: authBusiness, user: authUser, branches, selectedBranch } = useAppSelector((state) => state.auth);

  const queryParams = new URLSearchParams(location.search);
  const editId = queryParams.get("id");
  const createFromOrderId = queryParams.get("orderId");

  const [loadingInitial, setLoadingInitial] = useState(!!editId || !!createFromOrderId);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState<string | number>(selectedBranch?.id || "");

  const activeBranch = useMemo(
    () => (branches || []).find((b) => String(b.id) === String(selectedBranchId)),
    [branches, selectedBranchId]
  );
  const studioGstNumber = (
    activeBranch?.gst_number ||
    (selectedBranch?.id && String(selectedBranch.id) === String(selectedBranchId) ? selectedBranch.gst_number : "") ||
    authBusiness?.gst_number ||
    (authUser as any)?.gst_number ||
    ""
  ).trim();

  const prevSelectedBranchIdRef = useRef<number | null | undefined>(selectedBranch?.id);
  const isInvoiceLoadedRef = useRef(false);

  useEffect(() => {
    if (prevSelectedBranchIdRef.current !== selectedBranch?.id) {
      prevSelectedBranchIdRef.current = selectedBranch?.id;
      if (!editId || isInvoiceLoadedRef.current) {
        const nextId = selectedBranch?.id ? String(selectedBranch.id) : "";
        setSelectedBranchId(nextId);
        setFormData((p) => ({ ...p, branch_id: nextId ? Number(nextId) : null }));
      }
    }
  }, [selectedBranch?.id, editId]);

  const [formData, setFormData] = useState<InvoiceWritePayload>({
    branch_id: selectedBranch?.id || null,
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_place: "",
    customer_address: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    notes: "",
    without_price: false,
    add_gst_number: false,
    gst_number: "",
    add_terms_conditions: true,
    terms_and_conditions: authBusiness?.terms_and_conditions || "",
    discount_percent: "",
    tax_percent: "",
    line_items: [],
    installments: [],
  });

  const handleGstToggle = (checked: boolean) => {
    if (checked) {
      if (!studioGstNumber) {
        showErrorToast("Please add GST number in profile");
        setFormData((p) => ({ ...p, add_gst_number: false, gst_number: "" }));
        return;
      }
      setFormData((p) => ({ ...p, add_gst_number: true, gst_number: studioGstNumber }));
    } else {
      setFormData((p) => ({ ...p, add_gst_number: false, gst_number: "" }));
    }
  };

  const [grandTotalPreview, setGrandTotalPreview] = useState("0.00");

  useEffect(() => {
    const initForm = async () => {
      try {
        if (editId) {
          const res = await getInvoiceByIdAPI(editId);
          setInvoiceNo(res.invoice_no);
          if (res.branch_id) {
            setSelectedBranchId(String(res.branch_id));
          } else {
            setSelectedBranchId("");
          }
          isInvoiceLoadedRef.current = true;
          prevSelectedBranchIdRef.current = selectedBranch?.id;
          setFormData({
            branch_id: res.branch_id || null,
            customer_name: res.customer_name || "",
            customer_phone: res.customer_phone || "",
            customer_email: res.customer_email || "",
            customer_place: res.customer_place || "",
            customer_address: res.customer_address || "",
            invoice_date: res.invoice_date || "",
            due_date: res.due_date || "",
            notes: res.notes || "",
            without_price: res.without_price || false,
            add_gst_number: Boolean(res.add_gst_number),
            gst_number: res.gst_number || "",
            add_terms_conditions: res.add_terms_conditions !== undefined && res.add_terms_conditions !== null ? Boolean(res.add_terms_conditions) : false,
            terms_and_conditions: res.terms_and_conditions && typeof res.terms_and_conditions === "string" && res.terms_and_conditions.trim() ? res.terms_and_conditions : (authBusiness?.terms_and_conditions || ""),
            discount_percent: res.discount_percent || "",
            tax_percent: res.tax_percent || "",
            line_items: res.line_items || [],
            installments: res.installments || [],
          });
        } else if (createFromOrderId) {
          const res = await createInvoiceFromOrderAPI({ order_id: Number(createFromOrderId) });
          showSuccessToast("Invoice drafted from order successfully");
          navigate(`/dashboard/invoice/edit?id=${res.id}`, { replace: true });
        } else {
          const res = await getInvoiceNextNumberAPI();
          setInvoiceNo(res.invoice_no);
        }
      } catch (err) {
        showErrorToast("Failed to initialize form");
      } finally {
        setLoadingInitial(false);
      }
    };
    initForm();
  }, [editId, createFromOrderId, navigate]);

  useEffect(() => {
    if (authBusiness?.terms_and_conditions && !formData.terms_and_conditions && (!editId || !loadingInitial)) {
      setFormData((p) => ({ ...p, terms_and_conditions: authBusiness.terms_and_conditions || "" }));
    }
  }, [authBusiness?.terms_and_conditions, editId, loadingInitial, formData.terms_and_conditions]);

  useEffect(() => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + item.quantity * parseFloat(item.unit_price || "0"), 0);
    const discount = subtotal * (parseFloat(formData.discount_percent || "0") / 100);
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (parseFloat(formData.tax_percent || "0") / 100);
    const total = afterDiscount + tax;
    setGrandTotalPreview(isNaN(total) ? "0.00" : total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }, [formData.line_items, formData.discount_percent, formData.tax_percent]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.customer_phone) {
      showErrorToast("Customer name and phone are required.");
      return;
    }
    if (!formData.due_date) {
      showErrorToast("Due date is required.");
      return;
    }
    const cleanNum = cleanPhone(formData.customer_phone);
    if (cleanNum.length < 10 || cleanNum.length > 15) {
      showErrorToast("Phone number must be between 10 and 15 digits");
      return;
    }
    let installments: InvoiceInstallment[] = formData.installments || [];
    if (installments.length === 0) {
      installments = [
        {
          sequence: 1,
          percent: "100",
          label: "Installment 1",
          due_date: formData.due_date || formData.invoice_date,
          paid: false,
        },
      ];
    } else {
      const totalPercent = installments.reduce(
        (sum, item) => sum + parseFloat(item.percent || "0"),
        0
      );
      if (Math.abs(totalPercent - 100) >= 0.01) {
        showErrorToast("Installment percentages must add up to 100% before saving.");
        return;
      }
    }

    const payload: InvoiceWritePayload = {
      ...formData,
      branch_id: selectedBranchId ? Number(selectedBranchId) : null,
      add_gst_number: formData.add_gst_number,
      gst_number: formData.add_gst_number ? (studioGstNumber || formData.gst_number || "") : "",
      add_terms_conditions: formData.add_terms_conditions,
      terms_and_conditions: formData.add_terms_conditions ? (formData.terms_and_conditions || authBusiness?.terms_and_conditions || null) : null,
      customer_phone: cleanNum,
      discount_percent: formData.discount_percent || "0",
      tax_percent: formData.tax_percent || "0",
      installments,
    };

    try {
      if (editId) {
        await updateInvoiceAPI(editId, payload);
        showSuccessToast("Invoice updated successfully");
      } else {
        const createdInvoice = await dispatch(createInvoiceAPI(payload)).unwrap();
        showSuccessToast("Invoice created successfully");
        if (createdInvoice.installments && createdInvoice.installments.length > 0) {
          const firstInstallment = createdInvoice.installments[0];
          if (firstInstallment.id !== undefined && firstInstallment.id !== null) {
            try {
              const hostName = getHostUrl();
              await createPaymentLinkAPI(createdInvoice.id, firstInstallment.id, { host_name: hostName });
              await sharePaymentLinkWhatsAppAPI(createdInvoice.id, firstInstallment.id);
              showSuccessToast("First installment link sent to customer on WhatsApp 🎉");
            } catch (err: any) {
              console.error("Auto link share failed:", err);
              showErrorToast("Failed to generate or send payment link for first installment");
            }
          }
        }
      }
      navigate("/dashboard/invoice");
    } catch (err: any) {
      showErrorToast(getApiErrorMessage(err, "Failed to save invoice"));
    }
  };

  if (loadingInitial) {
    return (
      <PageContainer style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
        <Loader size={32} className="spin" color="#c94daa" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormHero>
        <FormHeroGlow />
        <FormHeroRow>
          <FormBackButton type="button" onClick={() => navigate("/dashboard/invoice")} aria-label="Go back">
            <ArrowLeft size={18} />
          </FormBackButton>
          <FormHeroText>
            <FormHeroBadge>
              <Sparkles size={11} />
              Billing
            </FormHeroBadge>
            <PageTitle className="linear-text">
              {editId ? `Edit Invoice${invoiceNo ? ` (${invoiceNo})` : ""}` : "Create Invoice"}
            </PageTitle>
            <FormHeroSubtitle>
              {editId
                ? "Update customer details, line items, pricing, and installment schedule."
                : "Build an invoice with line items, tax, discounts, and payment installments."}
            </FormHeroSubtitle>
          </FormHeroText>
        </FormHeroRow>
      </FormHero>

      <FormScrollContainer>
        <form onSubmit={handleSubmit}>
          <WithoutPriceBanner>
            <WithoutPriceToggleWrapper>
              <WithoutPriceText>Create invoice without price</WithoutPriceText>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={formData.without_price}
                  onChange={(e) => setFormData((p) => ({ ...p, without_price: e.target.checked }))}
                />
                <span />
              </ToggleSwitch>
            </WithoutPriceToggleWrapper>
            <WithoutPriceInfoBox>
              <Info size={18} color="#c94daa" />
              <span>Hides unit price and amount fields on line items. You can add pricing later when ready.</span>
            </WithoutPriceInfoBox>
          </WithoutPriceBanner>

          <WithoutPriceBanner style={{ marginTop: "-8px" }}>
            <WithoutPriceToggleWrapper>
              <WithoutPriceText>Add GST number</WithoutPriceText>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={Boolean(formData.add_gst_number)}
                  onChange={(e) => handleGstToggle(e.target.checked)}
                />
                <span />
              </ToggleSwitch>
            </WithoutPriceToggleWrapper>

            <WithoutPriceInfoBox>
              <Receipt size={18} color="#c94daa" />
              <span>
                {studioGstNumber
                  ? `Includes your studio's GSTIN (${studioGstNumber}) on this invoice.`
                  : "Includes your studio's GSTIN on this invoice (Add GST number in Profile)."}
              </span>
            </WithoutPriceInfoBox>
          </WithoutPriceBanner>

          <WithoutPriceBanner style={{ marginTop: "-8px" }}>
            <WithoutPriceToggleWrapper>
              <WithoutPriceText>Add Terms & Conditions</WithoutPriceText>
              <ToggleSwitch>
                <input
                  type="checkbox"
                  checked={formData.add_terms_conditions ?? true}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData((p) => ({
                      ...p,
                      add_terms_conditions: checked,
                      terms_and_conditions:
                        checked && !p.terms_and_conditions && authBusiness?.terms_and_conditions
                          ? authBusiness.terms_and_conditions
                          : p.terms_and_conditions,
                    }));
                  }}
                />
                <span />
              </ToggleSwitch>
            </WithoutPriceToggleWrapper>

            <WithoutPriceInfoBox>
              <FileText size={18} color="#c94daa" />
              <span>
                {formData.add_terms_conditions ?? true
                  ? "Terms & conditions will be included on the invoice document and PDF."
                  : "Exclude terms & conditions from this invoice."}
              </span>
            </WithoutPriceInfoBox>
          </WithoutPriceBanner>

          {(formData.add_terms_conditions ?? true) && (
            <div style={{ marginTop: "-8px", marginBottom: "20px", padding: "14px 16px", background: "#fafafa", borderRadius: "14px", border: "1px solid rgba(201, 77, 170, 0.16)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "13px", fontWeight: 700, color: "#171717", fontFamily: "Montserrat, sans-serif" }}>
                  Terms & Conditions (Point by point)
                </span>
                {authBusiness?.terms_and_conditions && (
                  <button
                    type="button"
                    onClick={() => setFormData((p) => ({ ...p, terms_and_conditions: authBusiness.terms_and_conditions || "" }))}
                    style={{ background: "none", border: "none", color: "#c94daa", fontSize: "12px", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                  >
                    {!formData.terms_and_conditions ? "Load Studio Default Terms" : (formData.terms_and_conditions !== authBusiness.terms_and_conditions ? "Reset to Studio Default" : "")}
                  </button>
                )}
              </div>
              <textarea
                value={formData.terms_and_conditions ?? ""}
                onChange={(e) => setFormData((p) => ({ ...p, terms_and_conditions: e.target.value }))}
                placeholder="Enter terms and conditions (point by point)..."
                rows={4}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  borderRadius: "8px",
                  border: "1px solid rgba(23, 23, 23, 0.15)",
                  padding: "10px 12px",
                  fontSize: "13px",
                  fontFamily: "Montserrat, sans-serif",
                  lineHeight: "1.5",
                  color: "#171717",
                  background: "#ffffff",
                  resize: "vertical",
                  outline: "none"
                }}
              />
              <span style={{ fontSize: "11px", color: "rgba(23, 23, 23, 0.5)", marginTop: "4px", display: "block" }}>
                {authBusiness?.terms_and_conditions
                  ? "Pre-filled with your studio default terms. Edits made here apply only to this invoice."
                  : "Tip: You can set default terms for all documents anytime from the Invoices toolbar."}
              </span>
            </div>
          )}

          <FormCard>
            <SectionHeading>
              <SectionIcon><Users size={20} /></SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Customer details</SectionHeadingTitle>
                <SectionHeadingSub>Name, contact, and address for this invoice</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <RowGrid>
              <FormGroup>
                <FormLabel>Customer Name <Req>*</Req></FormLabel>
                <TextInput type="text" name="customer_name" value={formData.customer_name} onChange={handleChange} required placeholder="John Doe" />
              </FormGroup>
              <FormGroup>
                <FormLabel>Phone Number <Req>*</Req></FormLabel>
                <TextInput type="text" name="customer_phone" value={formData.customer_phone} onChange={handleChange} required placeholder="9876543210" maxLength={15} />
              </FormGroup>
              <FormGroup>
                <FormLabel>Email</FormLabel>
                <TextInput type="email" name="customer_email" value={formData.customer_email || ""} onChange={handleChange} placeholder="john@example.com" />
              </FormGroup>
            </RowGrid>
            <RowGrid style={{ marginTop: 16 }}>
              <FormGroup>
                <FormLabel>Place / City</FormLabel>
                <TextInput type="text" name="customer_place" value={formData.customer_place || ""} onChange={handleChange} placeholder="Bangalore" />
              </FormGroup>
              <FormGroup style={{ flex: 2 }}>
                <FormLabel>Full Address</FormLabel>
                <TextAreaInput name="customer_address" value={formData.customer_address || ""} onChange={handleChange} rows={1} placeholder="123 Street Name..." />
              </FormGroup>
            </RowGrid>
          </FormCard>

          <FormCard>
            <SectionHeading>
              <SectionIcon><Calendar size={20} /></SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Invoice details</SectionHeadingTitle>
                <SectionHeadingSub>Invoice number and billing dates</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <RowGrid>
              <FormGroup>
                <FormLabel>Invoice No</FormLabel>
                <DisabledInput value={invoiceNo || "—"} readOnly />
              </FormGroup>
              {branches && branches.length > 0 && (
                <FormGroup>
                  <FormLabel>Branch (Optional)</FormLabel>
                  <FormSelect
                    value={selectedBranchId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedBranchId(val);
                      setFormData((p) => ({ ...p, branch_id: val ? Number(val) : null }));
                    }}
                  >
                    <option value="">All Branches / Main Studio (HQ)</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.branch_name} {b.city ? `(${b.city})` : ''}
                      </option>
                    ))}
                  </FormSelect>
                </FormGroup>
              )}
              <FormGroup>
                <FormLabel>Invoice Date <Req>*</Req></FormLabel>
                <BrandDatePicker
                  name="invoice_date"
                  value={formData.invoice_date}
                  onChange={(v) => setFormData((prev) => ({ ...prev, invoice_date: v }))}
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Due Date <Req>*</Req></FormLabel>
                <BrandDatePicker
                  name="due_date"
                  value={formData.due_date || ""}
                  onChange={(v) => setFormData((prev) => ({ ...prev, due_date: v }))}
                  required
                />
              </FormGroup>
            </RowGrid>
          </FormCard>

          <SectionCard>
            <SectionHeading>
              <SectionIcon><Package size={20} /></SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Line items</SectionHeadingTitle>
                <SectionHeadingSub>Products or services included on this invoice</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>
            <LineItemEditor
              items={formData.line_items}
              onChange={(items) => setFormData(p => ({ ...p, line_items: items }))}
              withoutPrice={formData.without_price}
            />
          </SectionCard>

          <SectionCard>
            <SectionHeading>
              <SectionIcon><IndianRupee size={20} /></SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Pricing & totals</SectionHeadingTitle>
                <SectionHeadingSub>Discount, tax, and grand total preview</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <RowGrid>
              <FormGroup>
                <FormLabel>Discount (%)</FormLabel>
                <TextInput type="number" name="discount_percent" min={0} max={100} step="0.1" value={formData.discount_percent} onChange={handleChange} placeholder="Enter discount" />
              </FormGroup>
              <FormGroup>
                <FormLabel>Tax (%)</FormLabel>
                <TextInput type="number" name="tax_percent" min={0} max={100} step="0.1" value={formData.tax_percent} onChange={handleChange} placeholder="Enter tax" />
              </FormGroup>
              <FormGroup>
                <FormLabel>Grand Total Preview</FormLabel>
                <GrandTotalDisplay>₹{grandTotalPreview}</GrandTotalDisplay>
              </FormGroup>
            </RowGrid>
          </SectionCard>

          <SectionCard>
            <SectionHeading>
              <SectionIcon><CreditCard size={20} /></SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Installments</SectionHeadingTitle>
                <SectionHeadingSub>Split the invoice total into payment milestones</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>
            <InstallmentEditor
              installments={formData.installments || []}
              grandTotal={grandTotalPreview}
              onChange={(insts) => setFormData(p => ({ ...p, installments: insts }))}
            />
          </SectionCard>

          <FormCard>
            <SectionHeading>
              <SectionIcon><FileText size={20} /></SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Additional notes</SectionHeadingTitle>
                <SectionHeadingSub>Terms, payment instructions, or other remarks</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>
            <TextAreaInput name="notes" value={formData.notes || ""} onChange={handleChange} rows={3} placeholder="Terms and conditions, payment instructions..." />
          </FormCard>

          <EndFooter>
            <SubmitHint>
              {formData.without_price
                ? "Prices are hidden. Review details and installments, then save this invoice."
                : "Review line items, pricing, and installments, then save this invoice."}
            </SubmitHint>
            <SaveButton type="submit" disabled={submitting}>
              {submitting ? <Loader size={16} className="spin" /> : <Save size={16} />}
              {submitting ? "Saving..." : "Save Invoice"}
            </SaveButton>
          </EndFooter>
        </form>
      </FormScrollContainer>
    </PageContainer>
  );
};

export default InvoiceFormPage;
